import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Badge } from '@/components/ui';
import { adminService } from '@/services/admin.service';
import type { AdminSettings } from '@/services/admin.service';

type SettingsSection = 'general' | 'billing' | 'notifications' | 'security';

export const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<AdminSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await adminService.getSettings();
        setSettings(data);
        setFormData(data);
      } catch {
        setError('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (section: keyof AdminSettings, field: string, value: unknown) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!formData) return;

    setIsSaving(true);
    setError(null);

    try {
      const updated = await adminService.updateSettings(formData);
      setSettings(updated);
      setFormData(updated);
      setHasChanges(false);
      setSuccessMessage('Settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      setError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setFormData(settings);
      setHasChanges(false);
    }
  };

  const sections: { id: SettingsSection; label: string; icon: React.ReactNode }[] = [
    {
      id: 'general',
      label: 'General',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      id: 'security',
      label: 'Security',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-secondary-200 rounded mb-6"></div>
          <div className="h-96 bg-secondary-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
        <p className="text-danger-700">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-success-700">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-success-500 hover:text-success-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-danger-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Settings</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Configure platform settings and preferences
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-3">
            <Badge variant="warning">Unsaved changes</Badge>
            <Button variant="secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={handleSave} isLoading={isSaving}>
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card padding="none">
            <nav className="py-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600'
                      : 'text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  {section.icon}
                  <span className="font-medium">{section.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          {/* General Settings */}
          {activeSection === 'general' && (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-secondary-900">
                  General Settings
                </h2>
                <p className="mt-1 text-sm text-secondary-500">
                  Basic platform configuration
                </p>
              </Card.Header>
              <Card.Body>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Input
                    label="Platform Name"
                    value={formData.general.platformName}
                    onChange={(e) =>
                      handleChange('general', 'platformName', e.target.value)
                    }
                  />
                  <Input
                    label="Support Email"
                    type="email"
                    value={formData.general.supportEmail}
                    onChange={(e) =>
                      handleChange('general', 'supportEmail', e.target.value)
                    }
                  />
                  <Input
                    label="Support Phone"
                    value={formData.general.supportPhone}
                    onChange={(e) =>
                      handleChange('general', 'supportPhone', e.target.value)
                    }
                  />
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                      Address
                    </label>
                    <textarea
                      value={formData.general.address}
                      onChange={(e) =>
                        handleChange('general', 'address', e.target.value)
                      }
                      rows={3}
                      className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Billing Settings */}
          {activeSection === 'billing' && (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-secondary-900">
                  Billing Settings
                </h2>
                <p className="mt-1 text-sm text-secondary-500">
                  Configure billing and pricing
                </p>
              </Card.Header>
              <Card.Body>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Input
                    label="GST Percentage (%)"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.billing.gstPercentage.toString()}
                    onChange={(e) =>
                      handleChange(
                        'billing',
                        'gstPercentage',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                  <Input
                    label="TDS Percentage (%)"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.billing.tdsPercentage.toString()}
                    onChange={(e) =>
                      handleChange(
                        'billing',
                        'tdsPercentage',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                  <Input
                    label="QLead Cost ($)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billing.qLeadCost.toString()}
                    onChange={(e) =>
                      handleChange(
                        'billing',
                        'qLeadCost',
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                  <Input
                    label="Minimum Topup Amount ($)"
                    type="number"
                    min="0"
                    value={formData.billing.minTopupAmount.toString()}
                    onChange={(e) =>
                      handleChange(
                        'billing',
                        'minTopupAmount',
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                  <Input
                    label="Maximum Topup Amount ($)"
                    type="number"
                    min="0"
                    value={formData.billing.maxTopupAmount.toString()}
                    onChange={(e) =>
                      handleChange(
                        'billing',
                        'maxTopupAmount',
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Notification Settings */}
          {activeSection === 'notifications' && (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-secondary-900">
                  Notification Settings
                </h2>
                <p className="mt-1 text-sm text-secondary-500">
                  Configure email notifications
                </p>
              </Card.Header>
              <Card.Body>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary-900">
                        Welcome Email
                      </p>
                      <p className="text-sm text-secondary-500">
                        Send welcome email to new users
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications.welcomeEmailEnabled}
                        onChange={(e) =>
                          handleChange(
                            'notifications',
                            'welcomeEmailEnabled',
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary-900">
                        Topup Approval Email
                      </p>
                      <p className="text-sm text-secondary-500">
                        Notify users when topup is approved/rejected
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications.topupApprovalEmailEnabled}
                        onChange={(e) =>
                          handleChange(
                            'notifications',
                            'topupApprovalEmailEnabled',
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary-900">
                        Password Reset Email
                      </p>
                      <p className="text-sm text-secondary-500">
                        Send password reset instructions
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications.passwordResetEmailEnabled}
                        onChange={(e) =>
                          handleChange(
                            'notifications',
                            'passwordResetEmailEnabled',
                            e.target.checked
                          )
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="pt-4 border-t border-secondary-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-medium text-secondary-900">
                          Low Balance Alert
                        </p>
                        <p className="text-sm text-secondary-500">
                          Alert users when balance falls below threshold
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.notifications.lowBalanceAlertEnabled}
                          onChange={(e) =>
                            handleChange(
                              'notifications',
                              'lowBalanceAlertEnabled',
                              e.target.checked
                            )
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    {formData.notifications.lowBalanceAlertEnabled && (
                      <Input
                        label="Low Balance Threshold ($)"
                        type="number"
                        min="0"
                        value={formData.notifications.lowBalanceThreshold.toString()}
                        onChange={(e) =>
                          handleChange(
                            'notifications',
                            'lowBalanceThreshold',
                            parseInt(e.target.value) || 0
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Security Settings */}
          {activeSection === 'security' && (
            <Card>
              <Card.Header>
                <h2 className="text-lg font-semibold text-secondary-900">
                  Security Settings
                </h2>
                <p className="mt-1 text-sm text-secondary-500">
                  Configure password and session policies
                </p>
              </Card.Header>
              <Card.Body>
                <div className="space-y-6">
                  <h3 className="text-sm font-semibold text-secondary-800 uppercase tracking-wider">
                    Password Policy
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Input
                      label="Minimum Password Length"
                      type="number"
                      min="6"
                      max="32"
                      value={formData.security.minPasswordLength.toString()}
                      onChange={(e) =>
                        handleChange(
                          'security',
                          'minPasswordLength',
                          parseInt(e.target.value) || 8
                        )
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requireUppercase"
                        checked={formData.security.requireUppercase}
                        onChange={(e) =>
                          handleChange('security', 'requireUppercase', e.target.checked)
                        }
                        className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="requireUppercase"
                        className="ml-2 text-sm text-secondary-700"
                      >
                        Require uppercase letters
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requireLowercase"
                        checked={formData.security.requireLowercase}
                        onChange={(e) =>
                          handleChange('security', 'requireLowercase', e.target.checked)
                        }
                        className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="requireLowercase"
                        className="ml-2 text-sm text-secondary-700"
                      >
                        Require lowercase letters
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requireNumbers"
                        checked={formData.security.requireNumbers}
                        onChange={(e) =>
                          handleChange('security', 'requireNumbers', e.target.checked)
                        }
                        className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="requireNumbers"
                        className="ml-2 text-sm text-secondary-700"
                      >
                        Require numbers
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="requireSpecialChars"
                        checked={formData.security.requireSpecialChars}
                        onChange={(e) =>
                          handleChange(
                            'security',
                            'requireSpecialChars',
                            e.target.checked
                          )
                        }
                        className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                      />
                      <label
                        htmlFor="requireSpecialChars"
                        className="ml-2 text-sm text-secondary-700"
                      >
                        Require special characters
                      </label>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-secondary-200">
                    <h3 className="text-sm font-semibold text-secondary-800 uppercase tracking-wider mb-4">
                      Session Settings
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <Input
                        label="Session Timeout (minutes)"
                        type="number"
                        min="5"
                        max="1440"
                        value={formData.security.sessionTimeoutMinutes.toString()}
                        onChange={(e) =>
                          handleChange(
                            'security',
                            'sessionTimeoutMinutes',
                            parseInt(e.target.value) || 30
                          )
                        }
                        helperText="How long before inactive sessions expire"
                      />
                      <Input
                        label="Max Login Attempts"
                        type="number"
                        min="1"
                        max="10"
                        value={formData.security.maxLoginAttempts.toString()}
                        onChange={(e) =>
                          handleChange(
                            'security',
                            'maxLoginAttempts',
                            parseInt(e.target.value) || 5
                          )
                        }
                        helperText="Failed attempts before account lockout"
                      />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-secondary-200 p-4 shadow-lg z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm text-secondary-600">
              You have unsaved changes
            </p>
            <div className="flex items-center space-x-3">
              <Button variant="secondary" onClick={handleReset}>
                Discard
              </Button>
              <Button onClick={handleSave} isLoading={isSaving}>
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
